import React, { useState, useEffect } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { useParams, useNavigate } from "react-router-dom";
import { getSupplierInvoiceById } from "../shared/mockInvoiceData";
import { invoiceStatus, paymentTerms } from "../../../constants";
import { formatDate } from "@telerik/kendo-intl";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  DownloadIcon,
  PrinterIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";

const SupplierInvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = () => {
    if (id === "new") {
      // Handle new invoice creation
      setInvoice(null);
    } else {
      const invoiceData = getSupplierInvoiceById(id);
      setInvoice(invoiceData);
    }
  };

  if (!invoice && id !== "new") {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-500">Loading invoice...</p>
        </div>
      </div>
    );
  }

  const statusConfig = invoiceStatus.find((s) => s.name === invoice?.status);

  const handleApprove = () => {
    alert("Approve functionality - to be implemented with API");
  };

  const handleReject = () => {
    alert("Reject functionality - to be implemented with API");
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/invoices/supplier")}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {invoice?.invoiceNumber || "New Invoice"}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Supplier: {invoice?.supplierName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {invoice?.status === "Pending Approval" && (
              <>
                <Button
                  onClick={handleApprove}
                  themeColor="success"
                  className="k-button k-button-md k-rounded-md"
                >
                  <CheckCircleIcon className="w-4 h-4 mr-2 inline" />
                  Approve
                </Button>
                <Button
                  onClick={handleReject}
                  themeColor="error"
                  className="k-button k-button-md k-rounded-md"
                >
                  <XCircleIcon className="w-4 h-4 mr-2 inline" />
                  Reject
                </Button>
              </>
            )}
            <Button
              onClick={handlePrint}
              className="k-button k-button-md k-rounded-md k-button-outline"
            >
              <PrinterIcon className="w-4 h-4 mr-2 inline" />
              Print
            </Button>
          </div>
        </div>

        {/* Status Badge */}
        {invoice && (
          <div className="flex items-center gap-4">
            <span
              className="px-4 py-2 text-sm font-semibold rounded-lg"
              style={{
                backgroundColor: statusConfig?.color + "20",
                color: statusConfig?.color,
              }}
            >
              {invoice.status}
            </span>
            {invoice.paidAmount > 0 && invoice.paidAmount < invoice.totalAmount && (
              <span className="text-sm text-gray-600">
                Paid: {invoice.currency} {invoice.paidAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs selectedIndex={selectedTab} onSelect={(index) => setSelectedTab(index)}>
        <TabList className="mb-4">
          <Tab>Invoice Details</Tab>
          <Tab>Line Items</Tab>
          <Tab>Documents</Tab>
          <Tab>Payment History</Tab>
        </TabList>

        {/* Invoice Details Tab */}
        <TabPanel>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Number
                </label>
                <div className="text-base font-semibold text-gray-900">
                  {invoice?.invoiceNumber}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Supplier Name
                </label>
                <div className="text-base text-gray-900">{invoice?.supplierName}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Number
                </label>
                <div className="text-base text-blue-600 hover:underline cursor-pointer">
                  {invoice?.jobNumber}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PO Number
                </label>
                <div className="text-base text-blue-600 hover:underline cursor-pointer">
                  {invoice?.poNumber}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Date
                </label>
                <div className="text-base text-gray-900">
                  {formatDate(new Date(invoice?.invoiceDate), "dd-MMM-yyyy")}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <div className="text-base text-gray-900">
                  {formatDate(new Date(invoice?.dueDate), "dd-MMM-yyyy")}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Terms
                </label>
                <div className="text-base text-gray-900">{invoice?.paymentTerms}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Type
                </label>
                <div className="text-base text-gray-900">{invoice?.invoiceType}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <div className="text-base text-gray-900">{invoice?.currency}</div>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="text-base text-gray-900">{invoice?.description}</div>
              </div>
            </div>

            {/* Amount Summary */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="max-w-md ml-auto space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">
                    {invoice?.currency} {invoice?.subtotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax Amount:</span>
                  <span className="font-medium">
                    {invoice?.currency} {invoice?.taxAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                  <span>Total Amount:</span>
                  <span className="text-blue-600">
                    {invoice?.currency} {invoice?.totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {invoice?.paidAmount > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Paid Amount:</span>
                      <span className="font-medium">
                        {invoice?.currency} {invoice?.paidAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-red-600">
                      <span>Outstanding:</span>
                      <span>
                        {invoice?.currency} {(invoice?.totalAmount - invoice?.paidAmount).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </TabPanel>

        {/* Line Items Tab */}
        <TabPanel>
          <div className="bg-white rounded-lg shadow">
            <Grid
              data={invoice?.lineItems || []}
              style={{ height: "500px" }}
            >
              <GridColumn field="id" title="No" width="80" />
              <GridColumn field="description" title="Description" width="auto" />
              <GridColumn
                field="quantity"
                title="Quantity"
                width="120"
                format="{0:n0}"
              />
              <GridColumn
                field="unitPrice"
                title="Unit Price"
                width="150"
                format="{0:c2}"
                cell={(props) => (
                  <td className="text-right">
                    {invoice?.currency} {props.dataItem.unitPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </td>
                )}
              />
              <GridColumn
                field="taxRate"
                title="Tax %"
                width="100"
                format="{0:n0}"
              />
              <GridColumn
                field="amount"
                title="Amount"
                width="150"
                cell={(props) => (
                  <td className="text-right font-semibold">
                    {invoice?.currency} {props.dataItem.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </td>
                )}
              />
            </Grid>
          </div>
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Attached Documents</h3>
            <div className="space-y-3">
              {invoice?.documents?.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="w-8 h-8 text-blue-500" />
                    <div>
                      <div className="font-medium text-gray-900">{doc.name}</div>
                      <div className="text-sm text-gray-500">{doc.type}</div>
                    </div>
                  </div>
                  <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                    <DownloadIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
              {(!invoice?.documents || invoice?.documents.length === 0) && (
                <p className="text-gray-500 text-center py-8">No documents attached</p>
              )}
            </div>
          </div>
        </TabPanel>

        {/* Payment History Tab */}
        <TabPanel>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Payment History</h3>
            {invoice?.paidAmount > 0 ? (
              <div className="space-y-3">
                <div className="p-4 border border-gray-200 rounded-lg bg-green-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-gray-900">Payment Received</div>
                      <div className="text-sm text-gray-500">
                        Date: {formatDate(new Date(), "dd-MMM-yyyy")}
                      </div>
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      {invoice?.currency} {invoice?.paidAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No payments recorded</p>
            )}
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default SupplierInvoiceDetails;
